/**
 * Invite Users Page (Step 2)
 * Main page for managing user invitations.
 * Clean code: single responsibility, composed hooks, presentational components.
 * SOLID: page orchestrates only; logic in hooks and constants.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { WizardActionBar } from '../../components/organizationWizard/WizardActionBar';
import { canInviteUsers } from '../../utils/accessScopeUtils';
import { BulkActionsBar } from '../../components/organizationLocations/BulkActionsBar';
import { InviteUserModal } from '../../components/users/InviteUserModal';
import { DeleteUserModal } from '../../components/users/DeleteUserModal';
import { UsersSearchList } from '../../components/users/UsersSearchList';
import { useFeedbackContext } from '../../context';
import MainPageLayout from '../../components/common/MainPageLayout';
import ROUTES from '../../constants/routes';
import {
  useInviteUsersQuery,
  useUserSelection,
  useInviteUsersModals,
  useInviteUsersActions,
} from './hooks';
import { PageHeader } from './components';
import { WIZARD_STEP } from './constants';
import { getStoredUser } from '../../utils/authStorageUtils';

function getOrganizationId(state) {
  return state?.auth?.user?.organization ?? localStorage.getItem('organizationId') ?? null;
}

export function InviteUsersPage() {
  const navigate = useNavigate();
  const { showToast } = useFeedbackContext();
  const organizationId = useSelector(getOrganizationId);
  const authUser = useSelector((state) => state.auth.user);
  const effectiveUser = authUser?.data ?? authUser ?? getStoredUser();

  useEffect(() => {
    if (!canInviteUsers(effectiveUser)) {
      navigate(ROUTES.ORGANIZATION_CONFIGURATION, { replace: true });
    }
  }, [effectiveUser, navigate]);

  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  const { refetchUsers, handleReady } = useInviteUsersQuery();

  const {
    selectedUserIds,
    setSelectedUserIds,
    handleToggleSelectUser,
    handleSelectAll,
    removeFromSelection,
  } = useUserSelection();

  const {
    isModalOpen,
    editingUser,
    openModal,
    closeModal,
    isDeleteModalOpen,
    userToDelete,
    openDeleteModal,
    closeDeleteModal,
  } = useInviteUsersModals();

  const {
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleDeleteSelected,
  } = useInviteUsersActions(showToast, refetchUsers, setSelectedUserIds);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;
    await handleDeleteUser(userToDelete.id, () => {
      closeDeleteModal();
      removeFromSelection(userToDelete.id);
    });
  }, [userToDelete, handleDeleteUser, closeDeleteModal, removeFromSelection]);

  const handleContinue = useCallback(() => {
    navigate(ROUTES.ORGANIZATION_CONFIGURATION);
  }, [navigate]);

  const handleOpenBulkDeleteModal = useCallback(() => {
    setIsBulkDeleteModalOpen(true);
  }, []);

  const handleConfirmBulkDelete = useCallback(async () => {
    await handleDeleteSelected(selectedUserIds);
    setIsBulkDeleteModalOpen(false);
  }, [handleDeleteSelected, selectedUserIds]);

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="w-full">
        <div className="max-w-[1400px] mx-auto px-6 pt-4 pb-32">
          <PageHeader />

          <UsersSearchList
            organizationId={organizationId}
            selectedUserIds={selectedUserIds}
            onSelectUser={handleToggleSelectUser}
            onSelectAll={handleSelectAll}
            onEditUser={openModal}
            onDeleteUser={openDeleteModal}
            onReady={handleReady}
            onHasUsersChange={() => {}}
            onUsersChange={() => {}}
            onInviteClick={() => openModal()}
          />
        </div>

        <WizardActionBar
          currentStep={WIZARD_STEP.CURRENT}
          totalSteps={WIZARD_STEP.TOTAL}
          onContinue={handleContinue}
          stepName={WIZARD_STEP.NAME}
          continueText="Back to overview"
        />

        {selectedUserIds.size > 0 && (
          <BulkActionsBar
            selectedCount={selectedUserIds.size}
            onDelete={handleOpenBulkDeleteModal}
            onClear={() => setSelectedUserIds(new Set())}
            countLabel="users"
          />
        )}

        <InviteUserModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={editingUser ? (data) => handleUpdateUser(editingUser.id, data) : handleCreateUser}
          editingUser={editingUser}
        />

        {userToDelete && (
          <DeleteUserModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onConfirm={handleConfirmDelete}
            user={userToDelete}
          />
        )}

        <DeleteUserModal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => setIsBulkDeleteModalOpen(false)}
          onConfirm={handleConfirmBulkDelete}
          users={Array.from(selectedUserIds, (id) => ({ id }))}
        />
      </div>
    </MainPageLayout>
  );
}
